import type { LearnerAssignmentKind, LearnerAssignmentStatus } from "@sol/sdk";

export enum AnalyticsEvent {
    PagePrint = "sol.page.print",
    UserIdentified = "sol.user.identified",
    OptOut = "sol.optOut",
    OptIn = "sol.optIn",
    LoginSuccess = "sol.login.success",
    LoginFailure = "sol.login.failure",
    AssignmentListFiltered = "sol.assignmentList.filtered",
    AssignmentListLayoutChanged = "sol.assignmentList.layoutChanged",
    AssignmentOpened = "sol.assignment.opened",
    AssignmentResourceOpened = "sol.assignment.resourceOpened",
    AssignmentChatOpened = "sol.assignment.chatOpened",
    AssignmentChatClosed = "sol.assignment.chatClosed",
    AssignmentHandInOpened = "sol.assignment.handInOpened",
    AssignmentHandInCancelled = "sol.assignment.handInCancelled",
    AssignmentHandInFailed = "sol.assignment.handInFailed",
    AssignmentReflectiveStartOpened = "sol.assignment.reflectiveStartOpened",
    AssignmentReflectiveStartCancelled = "sol.assignment.reflectiveStartCancelled",
}

type NoProperties = Record<string, never>;

export type PageInfo = Partial<
    Pick<Location, "href" | "protocol" | "host" | "hostname" | "port" | "pathname" | "search" | "hash" | "origin">
> &
    Partial<Pick<Document, "title" | "referrer">>;

/**
 * What each event carries, on top of the application infos and of the scope it is tracked in.
 *
 * Only identifiers, statuses and counts: never a name, an email or what the learner wrote.
 */
export type AnalyticsEventProperties = {
    [AnalyticsEvent.PagePrint]: { page: PageInfo; timestamp: number };
    [AnalyticsEvent.UserIdentified]: { identity?: { userId: string; traits: { role: string } } };
    [AnalyticsEvent.OptOut]: { cookieConsent: false };
    [AnalyticsEvent.OptIn]: { cookieConsent: true };
    [AnalyticsEvent.LoginSuccess]: NoProperties;
    [AnalyticsEvent.LoginFailure]: NoProperties;
    [AnalyticsEvent.AssignmentListFiltered]: { status: LearnerAssignmentStatus | "all" };
    [AnalyticsEvent.AssignmentListLayoutChanged]: { layout: "grid" | "list" };
    [AnalyticsEvent.AssignmentOpened]: { msSinceStatusChange?: number };
    [AnalyticsEvent.AssignmentResourceOpened]: { resourceType: "media" | "link" };
    [AnalyticsEvent.AssignmentChatOpened]: { channel: "ai" | "user" };
    [AnalyticsEvent.AssignmentChatClosed]: { channel: "ai" | "user" };
    [AnalyticsEvent.AssignmentHandInOpened]: NoProperties;
    [AnalyticsEvent.AssignmentHandInCancelled]: { step: number; stepCount: number; attachedCount: number };
    [AnalyticsEvent.AssignmentHandInFailed]: NoProperties;
    [AnalyticsEvent.AssignmentReflectiveStartOpened]: NoProperties;
    [AnalyticsEvent.AssignmentReflectiveStartCancelled]: NoProperties;
};

/** Events without properties can be tracked with the event alone */
export type TrackArguments<E extends AnalyticsEvent> = NoProperties extends AnalyticsEventProperties[E]
    ? [properties?: AnalyticsEventProperties[E]]
    : [properties: AnalyticsEventProperties[E]];

/** Added to every event tracked on the page of one assignment, so a funnel can follow a single brief */
export type AssignmentAnalyticsContext = {
    assignmentId: string;
    assignmentKind: LearnerAssignmentKind;
    assignmentStatus: LearnerAssignmentStatus;
    isReflective: boolean;
};

/** The properties a scope adds to the events tracked inside it */
export type AnalyticsScopeContext = AssignmentAnalyticsContext;
